import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  Cell,
} from 'recharts';
import { TaxonomyPayload } from '../../models/taxonomy';
import {
  InsightsDataService,
  AbcSummary,
  AbcValenceFilter,
  ABC_FUNCTIONS,
  ABC_FUNCTION_LABELS,
  ABC_FUNCTION_COLOURS,
  ABC_UNCLASSIFIED_COLOUR,
  ABC_UNCLASSIFIED_LABEL,
  ABC_FREQUENCY_COLOUR,
} from '../../services/insightsDataService';
import './charts.scss';

const AXIS = '#7e8a78';
const GRID = '#e6e0d2';

// Below this many episodes the charts are indicative only — negatives are sparse,
// so one incident must never read as a trend.
const SMALL_N = 5;

// ── Helpers ──────────────────────────────────────────────────────────────────

function addDays(d: Date, n: number): Date {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  c.setDate(c.getDate() + n);
  return c;
}

function mondayOf(d: Date): Date {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (c.getDay() + 6) % 7; // 0 = Monday
  return addDays(c, -day);
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The bucket start-dates spanning the range (Postgres date_trunc aligns to Monday
// weeks / first-of-month), so the trends show empty buckets as real zeros rather
// than collapsing the axis.
function bucketsInRange(fromDate: string, toDate: string, bucket: 'week' | 'month'): string[] {
  const out: string[] = [];
  const end = new Date(toDate + 'T00:00');
  if (bucket === 'month') {
    let cur = new Date(new Date(fromDate + 'T00:00').getFullYear(), new Date(fromDate + 'T00:00').getMonth(), 1);
    while (cur.getTime() <= end.getTime()) {
      out.push(iso(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
  } else {
    let cur = mondayOf(new Date(fromDate + 'T00:00'));
    while (cur.getTime() <= end.getTime()) {
      out.push(iso(cur));
      cur = addDays(cur, 7);
    }
  }
  return out;
}

function bucketLabel(start: string, bucket: 'week' | 'month'): string {
  const d = new Date(start + 'T00:00');
  return bucket === 'month'
    ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

function evidenceDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
}

// '' function → the neutral unclassified slot; any known function → its hue/label.
function funcLabel(fn: string): string {
  return (ABC_FUNCTION_LABELS as Record<string, string>)[fn] ?? ABC_UNCLASSIFIED_LABEL;
}
function funcColour(fn: string): string {
  return (ABC_FUNCTION_COLOURS as Record<string, string>)[fn] ?? ABC_UNCLASSIFIED_COLOUR;
}

function valenceWord(v: AbcValenceFilter): string {
  return v === 'negative' ? 'challenging' : v === 'positive' ? 'appropriate' : 'behaviour';
}

// The ABC chain, in the fixed order the notation requires. `key` is the API
// field (US spelling); `word` is what we show and announce (UK, matching the
// rest of the app). The letter is decorative — screen readers get `word`.
const ABC_PHASES: { key: 'antecedent' | 'behavior' | 'consequence'; letter: string; word: string }[] = [
  { key: 'antecedent', letter: 'A', word: 'Antecedent' },
  { key: 'behavior', letter: 'B', word: 'Behaviour' },
  { key: 'consequence', letter: 'C', word: 'Consequence' },
];

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  childId: number;
  childName: string | null;
  fromDate: string;
  toDate: string;
  valence: AbcValenceFilter;
  bucket: 'week' | 'month';
  taxonomy: TaxonomyPayload | null;
}

const BehaviourInsights: React.FC<Props> = ({ childId, childName, fromDate, toDate, valence, bucket, taxonomy }) => {
  const [summary, setSummary] = useState<AbcSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Evidence drill-down: filter the episode list by a clicked function.
  const [selectedFn, setSelectedFn] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    InsightsDataService.fetchAbcSummary(childId, fromDate, toDate, { valence, bucket })
      .then(d => { if (!cancelled) setSummary(d); })
      .catch(err => {
        console.error('Failed to load ABC summary', err);
        if (!cancelled) setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [childId, fromDate, toDate, valence, bucket]);

  // Clear the evidence filter when the scope changes.
  useEffect(() => { setSelectedFn(null); }, [childId, valence, fromDate, toDate]);

  // slug → label for staff-action + antecedent tags (from the taxonomy payload).
  const label = useMemo(() => {
    const m = taxonomy?.labels ?? {};
    return (slug: string) => m[slug] ?? slug;
  }, [taxonomy]);

  const totalEpisodes = useMemo(
    () => (summary?.episodes_by_bucket ?? []).reduce((s, r) => s + r.episodes, 0),
    [summary],
  );

  // Frequency-over-time — episodes per bucket, zero-filled across the range.
  const frequencyData = useMemo(() => {
    const byBucket = new Map((summary?.episodes_by_bucket ?? []).map(r => [r.bucket_start, r.episodes]));
    return bucketsInRange(fromDate, toDate, bucket).map(b => ({
      label: bucketLabel(b, bucket),
      episodes: byBucket.get(b) ?? 0,
    }));
  }, [summary, fromDate, toDate, bucket]);

  // Function breakdown — count per FBA function (+ unclassified), fixed order.
  const functionData = useMemo(() => {
    const byFn = new Map((summary?.function_distribution ?? []).map(r => [r.fn || '', r.count]));
    const rows = ABC_FUNCTIONS.map(fn => ({ fn: fn as string, label: ABC_FUNCTION_LABELS[fn], count: byFn.get(fn) ?? 0, fill: ABC_FUNCTION_COLOURS[fn] }));
    const unclassified = byFn.get('') ?? 0;
    if (unclassified > 0) rows.push({ fn: '', label: ABC_UNCLASSIFIED_LABEL, count: unclassified, fill: ABC_UNCLASSIFIED_COLOUR });
    return rows.filter(r => r.count > 0);
  }, [summary]);

  // Function mix over time — stacked per bucket, one segment per function. The FCT
  // story (challenging-escape giving way to appropriate-escape) in the Both view.
  const mixData = useMemo(() => {
    const buckets = bucketsInRange(fromDate, toDate, bucket);
    const seed = new Map<string, Record<string, number | string>>();
    buckets.forEach(b => seed.set(b, { label: bucketLabel(b, bucket) }));
    (summary?.function_by_bucket ?? []).forEach(r => {
      const slot = seed.get(r.bucket_start);
      if (!slot) return;
      const key = funcLabel(r.fn || '');
      slot[key] = ((slot[key] as number) ?? 0) + r.count;
    });
    return buckets.map(b => seed.get(b)!);
  }, [summary, fromDate, toDate, bucket]);

  // Which function series actually appear (so we don't render empty stacks).
  const mixSeries = useMemo(() => {
    const present = new Set((summary?.function_by_bucket ?? []).map(r => r.fn || ''));
    const series = ABC_FUNCTIONS.filter(fn => present.has(fn)).map(fn => ({ key: ABC_FUNCTION_LABELS[fn], colour: ABC_FUNCTION_COLOURS[fn] }));
    if (present.has('')) series.push({ key: ABC_UNCLASSIFIED_LABEL, colour: ABC_UNCLASSIFIED_COLOUR });
    return series;
  }, [summary]);

  // Antecedent triggers — tagged only (exclude the '' untagged bucket from the bars).
  const triggerData = useMemo(() => {
    return (summary?.antecedent_distribution ?? [])
      .filter(r => r.antecedent !== '')
      .map(r => ({ label: label(r.antecedent), count: r.count }))
      .sort((a, b) => b.count - a.count);
  }, [summary, label]);
  const untaggedCount = useMemo(
    () => (summary?.antecedent_distribution ?? []).find(r => r.antecedent === '')?.count ?? 0,
    [summary],
  );

  // "What was tried" — staff-action co-occurrence (top 8), non-causal.
  const staffData = useMemo(() => {
    return (summary?.staff_action_cooccurrence ?? [])
      .slice(0, 8)
      .map(r => ({ label: label(r.tag), count: r.count }));
  }, [summary, label]);

  const topFunction = functionData.find(r => r.fn !== '') ?? null;
  const topTrigger = triggerData[0] ?? null;

  // Trend arrow: last bucket vs the previous one (only meaningful with ≥2 buckets).
  const trend = useMemo(() => {
    const nz = frequencyData;
    if (nz.length < 2) return null;
    const last = nz[nz.length - 1].episodes;
    const prev = nz[nz.length - 2].episodes;
    if (last === prev) return { glyph: '→', cls: 'ci-muted' };
    return last > prev ? { glyph: '▲', cls: '' } : { glyph: '▼', cls: '' };
  }, [frequencyData]);

  const evidence = useMemo(() => {
    const list = summary?.evidence ?? [];
    return selectedFn == null ? list : list.filter(e => (e.function || '') === selectedFn);
  }, [summary, selectedFn]);

  // Filter chips are counted from the evidence rows, NOT function_distribution:
  // evidence is capped at 100, so distribution counts would promise rows the
  // list can't actually show.
  const fnFilters = useMemo(() => {
    const counts = new Map<string, number>();
    (summary?.evidence ?? []).forEach(e => {
      const k = e.function || '';
      counts.set(k, (counts.get(k) ?? 0) + 1);
    });
    const out = ABC_FUNCTIONS
      .filter(fn => (counts.get(fn) ?? 0) > 0)
      .map(fn => ({
        fn: fn as string,
        label: ABC_FUNCTION_LABELS[fn],
        colour: ABC_FUNCTION_COLOURS[fn],
        count: counts.get(fn)!,
      }));
    const unclassified = counts.get('') ?? 0;
    if (unclassified > 0) {
      out.push({
        fn: '',
        label: ABC_UNCLASSIFIED_LABEL,
        colour: ABC_UNCLASSIFIED_COLOUR,
        count: unclassified,
      });
    }
    return out;
  }, [summary]);

  const word = valenceWord(valence);
  const tipFormatter = ((v: any) => v) as any;

  if (loading && !summary) return <div className="atc-empty">Loading behaviour insights…</div>;
  if (error) return <div className="atc-empty">Couldn’t load behaviour insights. Please try again.</div>;
  if (totalEpisodes === 0) {
    return (
      <div className="atc-empty">
        No {word} behaviour episodes for {childName ?? 'this child'} in this period.
      </div>
    );
  }

  return (
    <>
      {totalEpisodes < SMALL_N && (
        <div className="atc-card__empty" style={{ marginBottom: 12 }}>
          Based on {totalEpisodes} episode{totalEpisodes === 1 ? '' : 's'} — indicative only, not a trend.
        </div>
      )}

      <div className="atc-summary">
        <div className="atc-stat">
          <div className="atc-stat__value">{totalEpisodes}</div>
          <div className="atc-stat__label">{word === 'behaviour' ? 'Episodes' : `${word[0].toUpperCase()}${word.slice(1)} episodes`}</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{trend ? trend.glyph : '—'}</div>
          <div className="atc-stat__label">Trend (latest {bucket})</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{topFunction ? topFunction.label : '—'}</div>
          <div className="atc-stat__label">Most common function</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{topTrigger ? topTrigger.label : '—'}</div>
          <div className="atc-stat__label">Top trigger</div>
        </div>
      </div>

      <div className="atc-grid">
        {/* 1. Frequency over time — single series (counts). */}
        <ChartCard title="Behaviour over time" subtitle={`${word[0].toUpperCase()}${word.slice(1)} episodes per ${bucket}`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={frequencyData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="label" stroke={AXIS} fontSize={11} interval={0} angle={-35} textAnchor="end" height={50} tickMargin={6} />
              <YAxis stroke={AXIS} fontSize={12} allowDecimals={false} />
              <Tooltip formatter={tipFormatter} />
              <Bar dataKey="episodes" fill={ABC_FREQUENCY_COLOUR} radius={[4, 4, 0, 0]} maxBarSize={48}>
                <LabelList dataKey="episodes" position="top" fontSize={11} formatter={((v: any) => (v > 0 ? v : '')) as any} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 2. Function breakdown — horizontal bar; click to filter the evidence. */}
        <ChartCard title="Why — behaviour function" subtitle="What the behaviour achieved (FBA) · click to see examples">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={functionData} layout="vertical" margin={{ top: 6, right: 36, left: 8, bottom: 6 }} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
              <XAxis type="number" stroke={AXIS} fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="label" stroke={AXIS} fontSize={12} width={90} />
              <Tooltip formatter={tipFormatter} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} onClick={(d: any) => {
                const fn = d?.payload?.fn as string | undefined;
                if (fn !== undefined) setSelectedFn(selectedFn === fn ? null : fn);
              }}>
                {functionData.map((row, i) => (
                  <Cell key={i} fill={row.fill} fillOpacity={selectedFn == null || selectedFn === row.fn ? 1 : 0.35} />
                ))}
                <LabelList dataKey="count" position="right" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 3. Function mix over time — stacked; the FCT story in the Both view. */}
        {mixSeries.length > 0 && (
          <ChartCard title="Function mix over time" subtitle={valence === 'all'
            ? 'How the function of behaviour shifts (challenging → appropriate)'
            : 'How the function of behaviour shifts over time'}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={mixData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="label" stroke={AXIS} fontSize={11} interval={0} angle={-35} textAnchor="end" height={50} tickMargin={6} />
                <YAxis stroke={AXIS} fontSize={12} allowDecimals={false} />
                <Tooltip formatter={tipFormatter} />
                <Legend verticalAlign="bottom" height={28} />
                {mixSeries.map(s => (
                  <Bar key={s.key} dataKey={s.key} stackId="fn" fill={s.colour} maxBarSize={48} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* 4. Triggers — antecedent patterns; near-empty until the taxonomy fills. */}
        <ChartCard title="Triggers" subtitle="What tends to come before — the antecedent">
          {triggerData.length === 0 ? (
            <div className="atc-card__empty">
              No trigger tags yet for this child. Triggers are captured on posts reviewed
              after this feature shipped, so this fills in over time
              {untaggedCount > 0 ? ` (${untaggedCount} episode${untaggedCount === 1 ? '' : 's'} not yet tagged).` : '.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={triggerData} layout="vertical" margin={{ top: 6, right: 36, left: 8, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" stroke={AXIS} fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke={AXIS} fontSize={11} width={150} />
                <Tooltip formatter={tipFormatter} />
                <Bar dataKey="count" fill="#4a3aa7" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="count" position="right" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* 5. What was tried — staff-action co-occurrence (non-causal). */}
        {staffData.length > 0 && (
          <ChartCard title="What was tried" subtitle="Staff strategies on these posts — co-occurrence, not cause">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={staffData} layout="vertical" margin={{ top: 6, right: 36, left: 8, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" stroke={AXIS} fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke={AXIS} fontSize={11} width={150} />
                <Tooltip formatter={tipFormatter} />
                <Bar dataKey="count" fill="#e87ba4" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="count" position="right" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* 6. Evidence — the actual episodes (desanitised). The function chips and
             the "Why — behaviour function" bars both drive `selectedFn`, so
             clicking either keeps the other in sync. */}
      <div className="ci-detail">
        <div className="ci-evbar">
          <span className="ci-evbar__crumb">
            Evidence — {evidence.length} episode{evidence.length === 1 ? '' : 's'}
            {(summary?.evidence.length ?? 0) >= 100 && ' · showing the most recent 100'}
          </span>

          {fnFilters.length > 1 && (
            <div className="ci-fnfilter" role="group" aria-label="Filter evidence by behaviour function">
              <button
                type="button"
                className={`ci-fnchip${selectedFn == null ? ' is-active' : ''}`}
                aria-pressed={selectedFn == null}
                onClick={() => setSelectedFn(null)}
              >
                <span>All</span>
                <span className="ci-fnchip__n">{summary?.evidence.length ?? 0}</span>
              </button>
              {fnFilters.map(f => (
                <button
                  key={f.fn || 'unclassified'}
                  type="button"
                  className={`ci-fnchip${selectedFn === f.fn ? ' is-active' : ''}`}
                  style={{ ['--fn' as string]: f.colour } as React.CSSProperties}
                  aria-pressed={selectedFn === f.fn}
                  onClick={() => setSelectedFn(selectedFn === f.fn ? null : f.fn)}
                >
                  <span className="ci-dot" aria-hidden="true" />
                  <span>{f.label}</span>
                  <span className="ci-fnchip__n">{f.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {evidence.length === 0 ? (
          <div className="ci-ep-empty">
            <p>No {funcLabel(selectedFn ?? '')} episodes in the most recent 100.</p>
            <button type="button" className="ci-ep-empty__action" onClick={() => setSelectedFn(null)}>
              Show all episodes
            </button>
          </div>
        ) : (
          <div className="ci-eplist">
            {evidence.map((e, i) => (
              <article className="ci-ep" key={`${e.post_id}-${i}`}>
                <header className="ci-ep__head">
                  <span className="ci-ep__date">{evidenceDate(e.post_date)}</span>
                  <span
                    className="ci-ep__fn"
                    style={{ ['--fn' as string]: funcColour(e.function) } as React.CSSProperties}
                  >
                    <span className="ci-dot" aria-hidden="true" />
                    {funcLabel(e.function)}
                  </span>
                  {e.antecedent_tag && (
                    <span className="ci-ep__trigger">{label(e.antecedent_tag)}</span>
                  )}
                </header>

                <div className="ci-abc">
                  {ABC_PHASES.map(p => (
                    <div className={`ci-abc__row ci-abc__row--${p.letter.toLowerCase()}`} key={p.key}>
                      <span className="ci-abc__node" aria-hidden="true">{p.letter}</span>
                      <span className="ci-abc__text">
                        <span className="visually-hidden">{p.word}: </span>
                        {e[p.key] || <span className="ci-muted">Not recorded</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

interface ChartCardProps { title: string; subtitle?: string; children: React.ReactNode; }
const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, children }) => (
  <div className="atc-card">
    <div className="atc-card__head">
      <h3 className="atc-card__title">{title}</h3>
      {subtitle && <div className="atc-card__sub">{subtitle}</div>}
    </div>
    <div className="atc-card__body">{children}</div>
  </div>
);

export default BehaviourInsights;
